import React from 'react';
import { Check, Users, HelpCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { TOURNAMENT_FORMATS } from '../registry/formatRegistry';
import { FORMAT_ICONS } from './iconMap';
import { slugify } from '../../utils/slugify';

// A real <a> can't nest inside a <button> (invalid HTML — button disallows
// interactive content), so the card itself is a div acting as a button
// (role/tabIndex/onKeyDown below) rather than a real <button> element, which
// leaves room for the separate "Learn more" link inside it.
const FormatCard = ({ format, selected, onSelect, index, disabled, reason }) => {
  const { theme } = useTheme();
  const Icon = FORMAT_ICONS[format.icon];

  const handleSelect = () => !disabled && onSelect(format.id);

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      title={disabled ? reason : undefined}
      style={{ animationDelay: `${index * 60}ms` }}
      className={`tf-animate-in group relative flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all duration-200 ${
        disabled
          ? `${theme.card} border cursor-not-allowed opacity-40`
          : selected
          ? `${theme.tabActive} ${theme.shadow} scale-[1.01] border-transparent cursor-pointer`
          : `${theme.card} ${theme.cardHover} border hover:-translate-y-0.5 hover:shadow-lg cursor-pointer`
      }`}
    >
      {selected && !disabled && (
        <span className={`absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full ${theme.buttonPrimary}`}>
          <Check size={14} strokeWidth={3} />
        </span>
      )}
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${selected && !disabled ? theme.buttonPrimary : theme.backgroundSecondary}`}>
        {Icon && <Icon size={22} />}
      </span>
      <div>
        <div className={`font-semibold ${theme.text}`}>{format.label}</div>
        <div className={`mt-0.5 text-xs font-medium uppercase tracking-wide ${theme.textMuted}`}>{format.tagline}</div>
      </div>
      <p className={`text-sm leading-snug ${theme.textSecondary}`}>{format.description}</p>
      <div className={`mt-auto flex w-full items-center justify-between gap-1.5 pt-1 text-xs ${disabled ? 'font-semibold text-amber-500' : theme.textMuted}`}>
        <span className="flex items-center gap-1.5">
          <Users size={13} />
          {disabled ? reason : `${format.minParticipants}+ ${format.participantLabel}`}
        </span>
        <a
          href={`/help#${slugify(format.label)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`flex flex-shrink-0 items-center gap-1 font-medium ${theme.link} ${theme.linkHover}`}
        >
          <HelpCircle size={13} />
          Learn more
        </a>
      </div>
    </div>
  );
};

/**
 * `eligibleFormats` — see formatEligibility.js's `getEligibleFormats` — is
 * this component's ONLY source of enabled/disabled state; it never derives
 * its own minParticipants/sport checks, so every eligibility rule lives in
 * exactly one place.
 */
const FormatPicker = ({ selectedFormatId, onSelect, eligibleFormats }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
    {TOURNAMENT_FORMATS.map((format, i) => {
      const { enabled, reason } = eligibleFormats[format.id];
      return (
        <FormatCard
          key={format.id}
          format={format}
          index={i}
          selected={selectedFormatId === format.id}
          onSelect={onSelect}
          disabled={!enabled}
          reason={reason}
        />
      );
    })}
  </div>
);

export default FormatPicker;
